import { useRef, type ReactNode } from "react";
import { Button } from "./Button";
import { ModalShell } from "./ModalShell";
import { useUiStore } from "../store/uiStore";

interface ConfirmModalProps {
  title: string;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  confirmVariant?: "danger" | "primary";
  isBusy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirm dialog; initial focus lands on Cancel so Enter never fires a destructive action by accident. */
export function ConfirmModal({ title, message, confirmLabel, cancelLabel = "Cancel", confirmVariant = "danger", isBusy = false, onConfirm, onCancel }: ConfirmModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  return (
    <ModalShell
      title={title}
      onClose={onCancel}
      initialFocusRef={cancelRef}
      size="sm"
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={onCancel} disabled={isBusy}>
            {cancelLabel}
          </Button>
          <Button variant={confirmVariant} onClick={onConfirm} isLoading={isBusy}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-sm text-gray-700">{message}</div>
    </ModalShell>
  );
}

/** Renders the confirm dialog requested through uiStore.requestConfirm. */
export function ConfirmDialogHost() {
  const pending = useUiStore((state) => state.confirm);
  const resolveConfirm = useUiStore((state) => state.resolveConfirm);

  if (!pending) return null;

  return <ConfirmModal title={pending.title} message={pending.message} confirmLabel={pending.confirmLabel} cancelLabel={pending.cancelLabel} confirmVariant={pending.variant} onConfirm={() => resolveConfirm(true)} onCancel={() => resolveConfirm(false)} />;
}
